import { useEffect, useRef } from "react";
import { animate, useInView } from "framer-motion";

/**
 * CountUp — animates a number from `from` to `to` when scrolled into view (or on mount when in stage).
 * Renders a span with tabular-nums for stable width.
 */
export const CountUp = ({
  from = 0,
  to,
  duration = 2.2,
  prefix = "",
  suffix = "",
  decimals = 0,
  separator = ",",
  className = "",
  startDelay = 0,
  trigger = true,
  "data-testid": testId,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px" });

  useEffect(() => {
    if (!trigger || !inView || !ref.current) return;
    const node = ref.current;
    const controls = animate(from, to, {
      duration,
      delay: startDelay,
      ease: [0.16, 1, 0.3, 1],
      onUpdate(value) {
        node.textContent = prefix + formatNumber(value, decimals, separator) + suffix;
      },
    });
    return () => controls.stop();
  }, [inView, from, to, duration, prefix, suffix, decimals, separator, startDelay, trigger]);

  return (
    <span
      ref={ref}
      className={`tnum ${className}`}
      data-testid={testId}
    >
      {prefix}
      {formatNumber(from, decimals, separator)}
      {suffix}
    </span>
  );
};

function formatNumber(value, decimals, separator) {
  const fixed = Number(value).toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return decPart ? `${withSep}.${decPart}` : withSep;
}

export default CountUp;
